import React, { useState } from 'react';
import PriModal from '../PriModal';

function PriSubjectsEditMarksModal({ subject, saving, onClose, onSave, t }) {
  const [marks, setMarks] = useState({
    totalTheoryMarks: subject.totalTheoryMarks ?? '',
    passingTheoryMarks: '',
    totalPracticalMarks: subject.totalPracticalMarks ?? '',
    passingPracticalMarks: ''
  });
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setMarks(previous => ({ ...previous, [field]: event.target.value }));
    setError('');
  };

  const handleSubmit = async () => {
    const totalTheoryMarks = Number(marks.totalTheoryMarks);
    const passingTheoryMarks = Number(marks.passingTheoryMarks);
    const totalPracticalMarks = Number(marks.totalPracticalMarks);
    const passingPracticalMarks = Number(marks.passingPracticalMarks);
    const hasPassingTheoryMarks = marks.passingTheoryMarks !== '';
    const hasPassingPracticalMarks = marks.passingPracticalMarks !== '';
    const effectivePassingTheoryMarks = hasPassingTheoryMarks
      ? passingTheoryMarks
      : Number(subject.passingTheoryMarks);
    const effectivePassingPracticalMarks = hasPassingPracticalMarks
      ? passingPracticalMarks
      : Number(subject.passingPracticalMarks);

    if (
      !Number.isInteger(totalTheoryMarks) ||
      totalTheoryMarks < 1
    ) {
      setError(t('subjectMarksMustBePositive'));
      return;
    }
    if (
      hasPassingTheoryMarks &&
      (!Number.isInteger(passingTheoryMarks) || passingTheoryMarks < 1)
    ) {
      setError(t('subjectMarksMustBePositive'));
      return;
    }
    if (effectivePassingTheoryMarks > totalTheoryMarks) {
      setError(t('passingMarksCannotExceedTotal'));
      return;
    }
    if (subject.hasPractical) {
      if (
        !Number.isInteger(totalPracticalMarks) ||
        totalPracticalMarks < 1
      ) {
        setError(t('subjectMarksMustBePositive'));
        return;
      }
      if (
        hasPassingPracticalMarks &&
        (!Number.isInteger(passingPracticalMarks) || passingPracticalMarks < 1)
      ) {
        setError(t('subjectMarksMustBePositive'));
        return;
      }
      if (effectivePassingPracticalMarks > totalPracticalMarks) {
        setError(t('passingPracticalMarksCannotExceedTotal'));
        return;
      }
    }

    const updatedMarks = {};
    if (totalTheoryMarks !== Number(subject.totalTheoryMarks)) {
      updatedMarks.totalTheoryMarks = totalTheoryMarks;
    }
    if (hasPassingTheoryMarks) {
      updatedMarks.passingTheoryMarks = passingTheoryMarks;
    }
    if (subject.hasPractical) {
      if (totalPracticalMarks !== Number(subject.totalPracticalMarks)) {
        updatedMarks.totalPracticalMarks = totalPracticalMarks;
      }
      if (hasPassingPracticalMarks) {
        updatedMarks.passingPracticalMarks = passingPracticalMarks;
      }
    }

    if (Object.keys(updatedMarks).length === 0) {
      setError(t('enterMarksToUpdate'));
      return;
    }

    await onSave(subject.id, updatedMarks);
  };

  const renderMarkField = (field, label) => (
    <div className="pri-form-field" key={field}>
      <label htmlFor={`subject-marks-${field}`}>{t(label)}</label>
      <input
        id={`subject-marks-${field}`}
        type="number"
        min="1"
        step="1"
        value={marks[field]}
        onChange={handleChange(field)}
        placeholder={field.startsWith('passing') ? t('enterPassingMarksPlaceholder') : undefined}
        disabled={saving}
        required
      />
    </div>
  );

  const subjectDetails = [
    { label: t('subject'), value: subject.subjectName || t('notAvailable') },
    { label: t('code'), value: subject.subjectCode || t('notAvailable') },
    {
      label: t('class'),
      value: subject.className || subject.schoolClass?.className || t('notAvailable')
    },
    {
      label: t('section'),
      value: subject.sectionName || subject.section?.sectionName || t('classLevelSubjects')
    }
  ];

  return (
    <PriModal
      title={`✏️ ${t('editSubjectMarks')}`}
      onClose={onClose}
      footer={
        <>
          <button className="pri-btn pri-btn-ghost" onClick={onClose} disabled={saving}>
            {t('cancel')}
          </button>
          <button className="pri-btn pri-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? t('saving') : t('saveChanges')}
          </button>
        </>
      }
    >
      <section className="pri-subject-edit-details" aria-label={t('subjectDetails')}>
        <div className="pri-subject-edit-details-heading">
          <span className="pri-subject-edit-details-icon" aria-hidden="true">📘</span>
          <div>
            <h4>{subject.subjectName || t('notAvailable')}</h4>
            <span>{t('subjectDetails')}</span>
          </div>
        </div>
        <dl className="pri-subject-edit-details-grid">
          {subjectDetails.map(({ label, value }) => (
            <div className="pri-subject-edit-detail" key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className="pri-subject-edit-marks-heading">
        <h4>{t('marksConfiguration')}</h4>
        <span>{t('marksEditableHint')}</span>
      </div>
      <div className="pri-form-row">
        {renderMarkField('totalTheoryMarks', 'totalTheoryMarksLabel')}
        {renderMarkField('passingTheoryMarks', 'passingTheoryMarksLabel')}
      </div>
      {subject.hasPractical && (
        <div className="pri-form-row">
          {renderMarkField('totalPracticalMarks', 'totalPracticalMarksLabel')}
          {renderMarkField('passingPracticalMarks', 'passingPracticalMarksLabel')}
        </div>
      )}
      {error && <p className="pri-error-message">{error}</p>}
    </PriModal>
  );
}

export default PriSubjectsEditMarksModal;
